import { fitMetaDescription, leadWithTail } from './meta-description';

/**
 * Legacy-compatible HDP metadata copy. Keep this independent from routes and
 * data fetching so every server-rendered representation can share the same
 * privacy and copy contract.
 */
const UNDISCLOSED_ADDRESS = '(Undisclosed Address)';
const APARTMENT_PROPERTY_TYPES = new Set(['large', 'garden', 'medium']);
const AMENITY_COPY: Record<string, string> = {
  pets: 'pet friendly',
  laundry: 'laundry',
  parking: 'parking',
  hvac: 'AC',
  gym: 'gym',
  pool: 'pool',
  security: 'security',
  appliances: 'appliances',
};

type ListingMetadataAddress = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  hideStreet?: boolean;
};

export type AmenityHighlight = {
  id?: string;
  display?: string;
  types?: Array<string>;
};

export type ListingMetadataListing = {
  active?: boolean;
  address?: ListingMetadataAddress;
  amenities?: { highlights?: Array<AmenityHighlight> };
  building?: boolean;
  displayName?: string;
  isApartmentBldg?: boolean;
  listingMinMaxPriceBeds?: { minPrice?: number; maxPrice?: number };
  pricing?: { summary?: { priceLow?: number; priceHigh?: number } };
  propertyType?: string;
  details?: { numUnits?: number };
  title?: string;
  trusted?: boolean;
  unit?: string;
};

export type ListingMetadataInput = {
  listing: ListingMetadataListing;
  units?: Array<unknown>;
  isBuildingPage?: boolean;
};

function nonEmpty(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

function appendPeriod(value: string): string {
  return value.endsWith('.') ? value : `${value}.`;
}

function capitalize(value: string): string {
  return value ? `${value[0]!.toUpperCase()}${value.slice(1)}` : value;
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatCurrency(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

function isUndisclosed(listing: ListingMetadataListing): boolean {
  return listing.address?.hideStreet === true;
}

function isApartmentBuilding(listing: ListingMetadataListing): boolean {
  return APARTMENT_PROPERTY_TYPES.has(listing.propertyType?.toLowerCase() ?? '');
}

function isGenuineBuilding({ listing, units = [], isBuildingPage = false }: ListingMetadataInput): boolean {
  return (
    isBuildingPage ||
    (listing.building === true &&
      (isApartmentBuilding(listing) || (listing.details?.numUnits ?? 0) > 1 || units.length > 1))
  );
}

function cityStateZip(address: ListingMetadataAddress | undefined): string {
  const city = nonEmpty(address?.city);
  const stateZip = [nonEmpty(address?.state), nonEmpty(address?.zip)].filter(Boolean).join(' ');
  return [city, stateZip].filter(Boolean).join(', ');
}

function publicLocation(listing: ListingMetadataListing): string {
  if (isUndisclosed(listing)) return cityStateZip(listing.address);
  return nonEmpty(listing.address?.street) ?? '';
}

/** Returns an identity safe for metadata, JSON-LD, image alt text, and breadcrumbs. */
export function getPublicListingName(
  listing: Pick<ListingMetadataListing, 'address' | 'displayName' | 'title'>,
): string {
  if (isUndisclosed(listing)) return UNDISCLOSED_ADDRESS;
  return nonEmpty(listing.displayName) ?? nonEmpty(listing.title) ?? nonEmpty(listing.address?.street) ?? 'Rental listing';
}

function getStartingPrice(listing: ListingMetadataListing): number | null {
  const summary = listing.pricing?.summary;
  const price = summary?.priceLow ?? summary?.priceHigh ?? listing.listingMinMaxPriceBeds?.minPrice ?? listing.listingMinMaxPriceBeds?.maxPrice;
  return isPositiveNumber(price) ? price : null;
}

function getLegacyAmenityDescription(listing: ListingMetadataListing): string | null {
  const highlights = listing.amenities?.highlights?.slice(0, 3) ?? [];
  if (highlights.length === 0) return null;

  const amenities = highlights.map((highlight, index) => {
    const first = index === 0;
    const types = highlight.types?.filter((type) => Boolean(type.trim())) ?? [];

    if (highlight.id === 'parking' && types[0]) {
      const value = `parking ${types[0]}`;
      return first ? capitalize(value) : value.toLowerCase();
    }
    if (highlight.id === 'appliances' && types[0]) {
      const value = [types[0], types[1]?.toLowerCase()].filter(Boolean).join(', ');
      return first ? capitalize(value) : value.toLowerCase();
    }

    const value = AMENITY_COPY[highlight.id ?? ''] ?? highlight.display?.toLowerCase() ?? '';
    return first ? capitalize(value) : value;
  }).filter(Boolean);

  return amenities.length > 0 ? `${amenities.join(', ')}, & more.` : null;
}

export function getListingPageTitle(listing: ListingMetadataListing, isBuildingPage = false): string {
  const address = listing.address;
  const street = nonEmpty(address?.street);
  const city = nonEmpty(address?.city);
  const stateZip = [nonEmpty(address?.state), nonEmpty(address?.zip)].filter(Boolean).join(' ');
  const displayName = nonEmpty(listing.displayName);
  const unit = isBuildingPage ? null : nonEmpty(listing.unit);

  if (isUndisclosed(listing)) {
    return `${[UNDISCLOSED_ADDRESS, city, stateZip].filter(Boolean).join(', ') || 'Listing'} | HotPads`;
  }

  if (isApartmentBuilding(listing) && displayName && displayName !== street) {
    const containsApartment = /apartment/i.test(displayName);
    const titleWithoutPrice = city
      ? `${displayName}${containsApartment ? ' in' : ' Apartments in'} ${city}`
      : displayName;
    const startingPrice = getStartingPrice(listing);
    const title = titleWithoutPrice.length <= 40 && startingPrice
      ? `${titleWithoutPrice} - ${formatCurrency(startingPrice)}+ per month`
      : titleWithoutPrice;
    return `${title} | HotPads`;
  }

  const addressTitle = [street ?? getPublicListingName(listing), city, stateZip].filter(Boolean).join(', ');
  const unitTitle = unit && street ? [`${street} Apt ${unit}`, city, stateZip].filter(Boolean).join(', ') : addressTitle;
  return `${unitTitle || 'Listing'} | HotPads`;
}

/**
 * The description copy for a listing, from the longest form to the shortest.
 * The street and the display name come from feed data and have no length limit,
 * so a long value can push the full copy over `META_DESCRIPTION_MAX_LENGTH`.
 * `buildListingPageMetadata` picks the first form that fits.
 */
function getListingDescriptionCandidates(input: ListingMetadataInput): [string, ...Array<string>] {
  const { listing, units = [] } = input;
  const location = publicLocation(listing);

  // The legacy component bypasses every other branch for hidden addresses so
  // displayName (which can be a street) cannot leak into a public surface.
  if (isUndisclosed(listing)) {
    return location
      ? leadWithTail(`Rental in ${appendPeriod(location)}`, 'View details and get in touch on HotPads.')
      : ['Rental listing. View details and get in touch on HotPads.'];
  }

  const propertyType = nonEmpty(listing.propertyType) ?? 'rental';
  const propertyNoun = listing.trusted || isApartmentBuilding(listing) ? 'Apartment' : capitalize(propertyType);
  const locationForCopy = location || getPublicListingName(listing);

  if (listing.active === false) {
    return leadWithTail(
      `${propertyNoun} at ${appendPeriod(locationForCopy)}`,
      'View historic property details, photos, street view and search nearby properties on the largest and most trusted rental site.',
    );
  }

  if (listing.trusted) {
    const name = getPublicListingName(listing);
    const lead = `See apartments for rent at ${name} located at ${appendPeriod(locationForCopy)}`;
    const amenities = getLegacyAmenityDescription(listing);
    // A long name plus a long street can overflow the lead too, so the last rung keeps the name only.
    return [
      ...leadWithTail(lead, amenities ?? 'View property details from the largest and most trusted rental site.'),
      `See apartments for rent at ${appendPeriod(name)}`,
    ];
  }

  if (isGenuineBuilding(input)) {
    return leadWithTail(
      `Check out the ${units.length} rentals available at this multi-unit condo building located at ${appendPeriod(locationForCopy)}`,
      'View detailed property information and get real-time updates.',
    );
  }

  return leadWithTail(
    `${propertyNoun} for rent at ${appendPeriod(locationForCopy)}`,
    'View property details, photos, street view, and get real-time updates with the largest and most trusted rental site.',
  );
}

/** The full description copy. It has no length bound; `buildListingPageMetadata` bounds the meta value. */
export function getListingPageDescription(input: ListingMetadataInput): string {
  return getListingDescriptionCandidates(input)[0];
}

export function buildListingPageMetadata(input: ListingMetadataInput): { title: string; description: string } {
  return {
    title: getListingPageTitle(input.listing, input.isBuildingPage),
    description: fitMetaDescription(getListingDescriptionCandidates(input)),
  };
}
